import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaPause, FaPlay } from 'react-icons/fa';
import styles from './IndustryCarousel.module.scss';
import { INDUSTRIES, SLIDE_HEIGHT, SLIDE_WIDTH } from './industries';

/** Drift speed, CSS px per second. One card passes roughly every 8 seconds. */
const SPEED_PX_PER_SEC = 55;

/**
 * How long the drift stands down after deliberate navigation. Hover and focus
 * cover mouse and keyboard, but a touch tap produces neither, so without this a
 * tap would be walked over immediately.
 */
const COOLDOWN_MS = 6000;

/** Roughly how long a native smooth scroll runs; the loop seam is held off
 *  for this window so it can't retarget an animation already in flight. */
const SMOOTH_MS = 700;

/**
 * The strip is rendered three times and the scroll position is kept parked in
 * the middle copy. Copy N and copy N+1 are pixel-identical, so shifting the
 * position by exactly one copy is invisible — that is what makes the loop
 * seamless. Three copies (rather than two) leaves a full strip of runway in
 * both directions, so even a hard swipe never reaches a real edge.
 */
const COPIES = 3;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

/** Geometry read straight off the DOM, so nothing here can drift out of step
 *  with the card width or gap declared in the stylesheet. */
type Metrics = { x0: number; contentWidth: number; pitch: number };

const IndustryCarousel = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const reducedMotion = useReducedMotion();

  const [activeIndex, setActiveIndex] = useState(0);
  const [liveMessage, setLiveMessage] = useState('');

  // Sticky user intent. Once someone presses pause it stays paused (WCAG 2.2.2);
  // nothing below is allowed to flip this back on by itself.
  const [isPlaying, setIsPlaying] = useState(() => !prefersReducedMotion());

  // Transient conditions that suspend the drift and then clear themselves.
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [inCooldown, setInCooldown] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden
  );

  const activeRef = useRef(0);
  const cooldownTimerRef = useRef<number>();
  const smoothUntilRef = useRef(0);

  useEffect(() => {
    if (reducedMotion) setIsPlaying(false);
  }, [reducedMotion]);

  // Cached rather than measured per frame: reading offsetLeft in the animation
  // loop, right after writing scrollLeft, is a forced-reflow trap. The geometry
  // only changes when the track resizes, so recompute it there instead.
  const metricsRef = useRef<Metrics | null>(null);

  const remeasure = useCallback((): Metrics | null => {
    const slides = slideRefs.current;
    const first = slides[0];
    const second = slides[1];
    const nextCopy = slides[INDUSTRIES.length];
    if (!first || !second || !nextCopy) return null;

    const x0 = first.offsetLeft;
    const contentWidth = nextCopy.offsetLeft - x0;
    const pitch = second.offsetLeft - x0;
    if (contentWidth <= 0 || pitch <= 0) return null;

    metricsRef.current = { x0, contentWidth, pitch };
    return metricsRef.current;
  }, []);

  /** Fold any position back into the middle copy's range. */
  const wrap = useCallback((position: number, m: Metrics) => {
    const { x0, contentWidth: cw } = m;
    const rel = position - x0 - cw;
    return x0 + cw + (((rel % cw) + cw) % cw);
  }, []);

  const syncActive = useCallback(
    (position: number, m: Metrics) => {
      const rel = position - m.x0 - m.contentWidth;
      const next =
        ((Math.round(rel / m.pitch) % INDUSTRIES.length) + INDUSTRIES.length) %
        INDUSTRIES.length;
      // Guarded so a 60fps scroll stream doesn't become a 60fps render stream.
      if (next !== activeRef.current) {
        activeRef.current = next;
        setActiveIndex(next);
      }
    },
    []
  );

  const beginCooldown = useCallback(() => {
    setInCooldown(true);
    window.clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = window.setTimeout(
      () => setInCooldown(false),
      COOLDOWN_MS
    );
  }, []);

  useEffect(() => () => window.clearTimeout(cooldownTimerRef.current), []);

  /** Park the position in the middle copy on mount, and keep the same industry
   *  in view when the card width changes at a new viewport size. */
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const place = () => {
      const m = remeasure();
      if (!m) return;
      track.scrollLeft = m.x0 + m.contentWidth + activeRef.current * m.pitch;
    };

    place();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(place);
      observer.observe(track);
    }
    return () => observer?.disconnect();
  }, [remeasure]);

  // Every scroll — ours, a swipe, a wheel, a drag — lands here.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onScroll = () => {
      const m = metricsRef.current;
      if (!m) return;

      // Holding the seam off during a native smooth scroll: shifting the
      // position mid-animation would leave the browser animating toward a stale
      // absolute target, which is the one case a viewer would actually see.
      if (Date.now() >= smoothUntilRef.current) {
        const wrapped = wrap(track.scrollLeft, m);
        if (Math.abs(wrapped - track.scrollLeft) > 0.5) {
          track.scrollLeft = wrapped;
        }
      }
      syncActive(track.scrollLeft, m);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, [wrap, syncActive]);

  // Don't animate a section nobody is looking at.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const suppressed =
    isHovered ||
    hasFocusWithin ||
    isDragging ||
    inCooldown ||
    !inView ||
    !pageVisible;

  /**
   * The drift. Position is written straight to the DOM each frame — no React
   * state is involved, so this costs nothing in renders.
   */
  useEffect(() => {
    if (!isPlaying || reducedMotion || suppressed) return;
    const track = trackRef.current;
    if (!track) return;

    let frame = 0;
    let last = 0;
    let position = track.scrollLeft;
    let lastWritten = position;

    const step = (timestamp: number) => {
      if (!last) last = timestamp;
      // Clamped so a stalled frame can't teleport the strip.
      const dt = Math.min((timestamp - last) / 1000, 0.05);
      last = timestamp;

      // If anything else moved the scroller since our last write, adopt it.
      if (Math.abs(track.scrollLeft - lastWritten) > 2) {
        position = track.scrollLeft;
      }

      const m = metricsRef.current;
      if (m) {
        position = wrap(position + SPEED_PX_PER_SEC * dt, m);
        track.scrollLeft = position;
        lastWritten = position;
      }

      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying, reducedMotion, suppressed, wrap]);

  const smoothScrollTo = useCallback(
    (left: number, announceIndex?: number) => {
      const track = trackRef.current;
      if (!track) return;

      beginCooldown();
      smoothUntilRef.current = reducedMotion ? 0 : Date.now() + SMOOTH_MS;
      track.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });

      if (announceIndex !== undefined) {
        setLiveMessage(
          `${INDUSTRIES[announceIndex].title}, ${announceIndex + 1} of ${INDUSTRIES.length}`
        );
      }
    },
    [beginCooldown, reducedMotion]
  );

  /** Arrows move exactly one card, in place — there is no end to run into. */
  const nudge = useCallback(
    (direction: 1 | -1) => {
      const track = trackRef.current;
      const m = remeasure();
      if (!track || !m) return;

      const next =
        ((activeRef.current + direction) % INDUSTRIES.length + INDUSTRIES.length) %
        INDUSTRIES.length;
      smoothScrollTo(track.scrollLeft + direction * m.pitch, next);
    },
    [remeasure, smoothScrollTo]
  );

  /** Jump to an industry via whichever of its three copies is nearest, so the
   *  travel is always short regardless of where the loop currently sits. */
  const goToIndustry = useCallback(
    (index: number) => {
      const track = trackRef.current;
      const m = remeasure();
      if (!track || !m) return;

      const from = track.scrollLeft;
      let best = m.x0 + m.contentWidth + index * m.pitch;
      for (let copy = 0; copy < COPIES; copy += 1) {
        const candidate = m.x0 + copy * m.contentWidth + index * m.pitch;
        if (Math.abs(candidate - from) < Math.abs(best - from)) best = candidate;
      }
      smoothScrollTo(best, index);
    },
    [remeasure, smoothScrollTo]
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Native arrow-scrolling on a focused scroller moves an arbitrary ~40px.
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      nudge(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      nudge(-1);
    }
  };

  // Mouse-only drag shim: native scroll containers handle touch and pen
  // perfectly but have no click-drag, which would leave `cursor: grab` a lie.
  // Touch is left strictly alone.
  const dragRef = useRef<{
    pointerId: number;
    lastX: number;
    distance: number;
  } | null>(null);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const track = trackRef.current;
    if (!track) return;

    dragRef.current = {
      pointerId: event.pointerId,
      lastX: event.clientX,
      distance: 0,
    };
    smoothUntilRef.current = 0; // a drag cancels any in-flight smooth scroll
    track.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    if (!drag || !track || event.pointerId !== drag.pointerId) return;

    // Incremental rather than measured from the drag origin: the loop seam can
    // shift scrollLeft by a whole copy mid-drag, which would strand an
    // origin-based delta and jerk the strip.
    const dx = event.clientX - drag.lastX;
    drag.lastX = event.clientX;
    drag.distance += Math.abs(dx);
    track.scrollLeft -= dx;
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    if (!drag || !track || event.pointerId !== drag.pointerId) return;

    dragRef.current = null;
    if (track.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
    if (drag.distance > 4) beginCooldown();
  };

  // Three copies of the strip; only the middle one is exposed to assistive
  // tech, so a screen reader hears 17 slides rather than 51.
  const slides = Array.from({ length: COPIES }, (_, copy) =>
    INDUSTRIES.map((item, index) => ({ item, index, copy }))
  ).flat();

  return (
    <section
      ref={sectionRef}
      className={styles.industryCarousel}
      aria-labelledby="industry-carousel-heading"
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <h2 id="industry-carousel-heading">Industry Experience</h2>
          <p className={styles.subtitle}>
            We bring expertise across multiple sectors with proven solutions
          </p>
        </div>

        <div
          className={styles.viewport}
          role="group"
          aria-roledescription="carousel"
          aria-label="Industry experience"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setHasFocusWithin(true)}
          onBlur={() => setHasFocusWithin(false)}
        >
          <div
            ref={trackRef}
            className={`${styles.track} ${isDragging ? styles.dragging : ''}`}
            tabIndex={0}
            aria-label="Industry slides. Use the left and right arrow keys to navigate."
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            {slides.map(({ item, index, copy }) => {
              const isPrimary = copy === 1;
              return (
                <div
                  key={`${copy}-${item.id}`}
                  ref={(el) => {
                    slideRefs.current[copy * INDUSTRIES.length + index] = el;
                  }}
                  className={styles.slide}
                  {...(isPrimary
                    ? {
                        role: 'group',
                        'aria-roledescription': 'slide',
                        'aria-label': `${index + 1} of ${INDUSTRIES.length}: ${item.title}`,
                      }
                    : { 'aria-hidden': true })}
                >
                  <img
                    src={item.imageUrl}
                    alt={isPrimary ? item.title : ''}
                    width={SLIDE_WIDTH}
                    height={SLIDE_HEIGHT}
                    draggable={false}
                    decoding="async"
                    loading={isPrimary && index < 5 ? 'eager' : 'lazy'}
                  />
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowPrev}`}
            onClick={() => nudge(-1)}
            aria-label="Previous industry"
          >
            <FaChevronLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowNext}`}
            onClick={() => nudge(1)}
            aria-label="Next industry"
          >
            <FaChevronRight aria-hidden="true" />
          </button>
        </div>

        <div className={styles.controls}>
          <div className={styles.dots}>
            {INDUSTRIES.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.dot} ${index === activeIndex ? styles.dotActive : ''}`}
                onClick={() => goToIndustry(index)}
                aria-label={`Show ${item.title}`}
                aria-current={index === activeIndex}
              />
            ))}
          </div>

          {!reducedMotion && (
            <button
              type="button"
              className={styles.playToggle}
              onClick={() => setIsPlaying((playing) => !playing)}
              aria-pressed={!isPlaying}
            >
              {isPlaying ? <FaPause aria-hidden="true" /> : <FaPlay aria-hidden="true" />}
              <span>{isPlaying ? 'Pause' : 'Play'} auto-scroll</span>
            </button>
          )}

          {/* Announced only for deliberate navigation — the drift stays silent,
              or this would chatter continuously. */}
          <p className={styles.srOnly} aria-live="polite">
            {liveMessage}
          </p>
        </div>
      </div>
    </section>
  );
};

export default IndustryCarousel;
